import React, { useState } from "react";
import { ChevronProgressSectionDown } from "src/assets/icons/chevron-progress-section-down";
import { ChevronProgressSectionUp } from "src/assets/icons/chevron-progress-section-up";
import type { Attachment, DigiFiCustomer } from "src/types/conditionsInbox";
import { CustomerDetailsSection } from "./CustomerDetailsSection";
import { CustomerActions } from "./CustomerActions";
import {
  CustomerCard,
  CustomerCardHeader,
  CustomerInfo,
  CustomerName,
  CustomerPhone,
  ChevronWrapper,
} from "./SearchSection.styles";
import { Attachments } from "./Attachments";

interface CustomerCardItemProps {
  attachments: Attachment[];
  renamedAttachments?: Record<string, string>;
  customer: DigiFiCustomer;
  expandedCard: string | null;
  isSent: boolean;
  isCurrentlySending: boolean;
  onToggleCard: (customer: DigiFiCustomer) => void;
  onSendFiles: (customer: DigiFiCustomer, attachmentsId: Set<string>) => void;
  selectedEmailId: string | null;
}

export const CustomerCardItem: React.FC<CustomerCardItemProps> = ({
  attachments,
  renamedAttachments,
  customer,
  expandedCard,
  isSent,
  isCurrentlySending,
  onToggleCard,
  onSendFiles,
  selectedEmailId,
}) => {
  const [selectedAttachmentsId, setSelectedAttachmentsId] = useState<
    Set<string>
  >(() => new Set(attachments.map(({ id }) => id)));

  const toggleAttachment = (event: React.MouseEvent<HTMLDivElement>) => {
    const { id } = event.currentTarget.dataset;
    event.stopPropagation();

    if (!id) {
      return;
    }

    setSelectedAttachmentsId((prevSelected) => {
      const next = new Set(prevSelected);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSendFiles = (customer: DigiFiCustomer) => {
    onSendFiles(customer, selectedAttachmentsId);
  };

  const isExpanded = expandedCard === customer.id;

  return (
    <CustomerCard
      key={customer.id}
      expanded={isExpanded}
      selected={isSent}
      onClick={() => onToggleCard(customer)}
    >
      <CustomerCardHeader>
        <CustomerInfo>
          <CustomerName>{customer.name}</CustomerName>
          <CustomerPhone>{customer.phone}</CustomerPhone>
        </CustomerInfo>
        <ChevronWrapper
          expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCard(customer);
          }}
        >
          {isExpanded ? (
            <ChevronProgressSectionUp />
          ) : (
            <ChevronProgressSectionDown />
          )}
        </ChevronWrapper>
      </CustomerCardHeader>

      {isExpanded && (
        <>
          <CustomerDetailsSection customer={customer} />
          <Attachments
            attachments={attachments}
            renamedAttachments={renamedAttachments}
            selectedAttachmentsId={selectedAttachmentsId}
            onClickAttachment={toggleAttachment}
          />
          <CustomerActions
            disabled={selectedAttachmentsId.size === 0}
            customer={customer}
            isSent={isSent}
            isCurrentlySending={isCurrentlySending}
            onSendFiles={handleSendFiles}
            selectedEmailId={selectedEmailId}
          />
        </>
      )}
    </CustomerCard>
  );
};
